/*
 *  Copyright (c) Qwoach LLC 2021. All rights reserved.
 *  Unauthorized copying of this file, via any medium is strictly prohibited
 *  Proprietary and confidential
 *  Created by Artem Bogush <artem@qwoach.com> on 29/10/2021
 */

import TagItem from "@shared/Tags/TagItem";
import {Type} from "class-transformer";
import {
	TaskJournalEntry,
	NoteJournalEntry,
	FormJournalEntry,
	LegalDocJournalEntry,
	ReminderJournalEntry
} from "./journalEntry-imports"
import {DisplayDestinationEnum} from "@shared/Journal/DisplayDestinationEnum";
import {TypeClass} from "@qwoach/class-transformer-wrapper";
import Discriminator from "@/utils/Discriminator";
import {JournalEntryType} from "@shared/Journal/JournalEntryType";
import QwoachFile from "@shared/Drive/QwoachFile";
import {arrayToTruthyObject, ellipsisString, stripHtmlTags} from "@/utils/helpers";
import {FilterableProperty} from "@shared/Filterable/PropertyDecorators";
import {QwoachFileType} from "@shared/Drive/QwoachFileType.js";
import {ClearanceLevelEnum} from "@shared/Profile/ClearanceLevelEnum.js";
import FilterableItem from "@shared/Filterable/FilterableItem.js";
import ClientProfile from "@shared/Profile/ClientProfile";

@TypeClass(Discriminator.create([
	{ value: () => NoteJournalEntry, name: JournalEntryType.NOTE },
	{ value: () => TaskJournalEntry, name: JournalEntryType.TASK },
	{ value: () => FormJournalEntry, name: JournalEntryType.FORM },
	{ value: () => LegalDocJournalEntry, name: JournalEntryType.LEGAL_DOC },
	{ value: () => ReminderJournalEntry, name: JournalEntryType.REMINDER },
]))
class JournalEntry extends FilterableItem {


	type: string = null

	summary: string = null
	description: string = null

	@FilterableProperty(tag => tag.title)
	@Type(() => TagItem)
	tagItemsArray: TagItem[] = [];

	@Type(() => QwoachFile)
	attachmentsArray: QwoachFile[] = [];
	attachmentIdsArray: string[] = []

	// displayDestinationArray: DisplayDestinationEnum[] = [] //deprecated since v2. Use displayDestination instead
	displayDestination = {} //determines visibility of this journal entry. I.e if it should be displayed in coach journal, client journal, etc: displayDestination.COACH_JOURNAL=true means it's only for coach journal. See DisplayDestinationEnum

	//isOwnedByClient: boolean = false //deprecated since v2. Use createdById instead
	createdById: string = null
	clearanceLevel: ClearanceLevelEnum = ClearanceLevelEnum.STANDARD



	isShared: boolean = true

	clientId: string = ""
	@FilterableProperty()
	clientName: string = ""

	packageId: string = null
	packageTitle: string = null
	isGroupEntry: boolean = false



	isClientEnabled: boolean = true

	isEditable: boolean = true

	attachedWorkflowId: string = null
	workflowStateItemId: string = null
	workflowTitle: string = null


	constructor(type = null) {
		super();
		this.type = type
	}

	getCurrentSchemaVersion() {
		return 3
	}

	migrateSchema(oldVersion, newVersion) {
		super.migrateSchema(oldVersion, newVersion)
		if(oldVersion < 2){
			if(Array.isArray(this.displayDestinationArray)){
				this.displayDestination = arrayToTruthyObject(this.displayDestinationArray)
			}
			if(this.isOwnedByClient){
				this.createdById = this.clientId
			}
		}
		if(oldVersion < 3){
			//this migration was performed in the backend under migrateTeamFeature migration
		}
	}

	getShortClientProfile(){
		let clientProfile = new ClientProfile()
		clientProfile.setId(this.clientId)
		clientProfile.name = this.clientName
		return clientProfile
	}


	// migrateFromV1ToV2(clientProfile){
	// 	let coachId = clientProfile.coachId
	//
	//
	// 	if(this.hasDisplayDestination(DisplayDestinationEnum.COACH_JOURNAL)){
	// 		this.addSharedWithById(coachId)
	// 	}
	// 	if(this.hasDisplayDestination(DisplayDestinationEnum.CLIENT_JOURNAL_FOR_COACH)){
	// 		this.addSharedWithById(coachId)
	// 	}
	// 	if(this.hasDisplayDestination(DisplayDestinationEnum.CLIENT_JOURNAL_FOR_CLIENT)){
	// 		this.addSharedWithById(this.clientId)
	// 	}
	//
	// }

	createQwoachEvent(){
		return null
	}

	addAttachmentArray(qwoachFileArray){
		for(let file of qwoachFileArray){
			this.addAttachment(file)
		}
	}

	hasAttachments(){
		return this.attachmentsArray.length > 0
	}

	removeAttachmentArray(qwoachFileArray){
		for(let file of qwoachFileArray){
			this.removeAttachment(file)
		}
	}

	addAttachment(qwoachFile){
		let index = this.attachmentsArray.findIndex(file => file.id === qwoachFile.id)
		let index2 = this.attachmentIdsArray.findIndex(id => id === qwoachFile.id)
		if(index2 < 0){
			this.attachmentIdsArray.push(qwoachFile.id)
		}
		if(index < 0){
			this.attachmentsArray.push(qwoachFile)
			return true
		}
		return false
	}
	removeAttachment(qwoachFile){
		let index = this.attachmentsArray.findIndex(file => file.id === qwoachFile.id)
		if(index >= 0){
			return this.removeAttachmentByIndex(index)
		}
		return false
	}
	removeAttachmentByIndex(index){
		let [file] = this.attachmentsArray.splice(index, 1)
		console.log("JournalEntry.removeAttachmentByIndex: index = ", index, ", file = ", file)
		if(file){
			let index2 = this.attachmentIdsArray.findIndex(id => file.id === id)
			if(index2){
				this.attachmentIdsArray.splice(index2, 1)
			}
			return true
		}
		return false
	}

	moveToNewClient(newClientId){
		if(this.clientIdOld){
			return false
		}
		this.clientIdOld = this.clientId
		this.clientId = newClientId

		// this.removeShredWithId(this.clientIdOld)
		// this.addSharedWithId(this.clientId)

		return true
	}

	assignFromPackageItem(packageItem){
		if(packageItem){
			this.packageId = packageItem.id
			this.packageTitle = packageItem.title
			this.isGroupEntry = packageItem.isGroupPackage()
			this.addSharedWithById(packageItem.id)
		}
	}
	assignFromClientProfile(clientProfile){
		if(clientProfile){
			this.clientId = clientProfile.id
			this.clientName = clientProfile.name
			this.addSharedWithById(clientProfile.assignedTo.map(profile => profile.id))
			// this.addSharedWithById(clientProfile.id)//we don't share here since for coach private journal we don't want to share with client
		}
	}

	assignFromFileRef(fileRef){
		switch (fileRef.type){
			case QwoachFileType.DRIVE_FILE:
			case QwoachFileType.LINK:
				this.description = fileRef.name
				break;
		}
	}

	// addSharedWithId(id){
	// 	let index = this.sharedWithIds.findIndex(idLocal => idLocal === id)
	// 	if(index < 0){
	// 		this.sharedWithIds.push(id)
	// 	}
	// }
	//
	// removeShredWithId(id){
	// 	let index = this.sharedWithIds.findIndex(idLocal => idLocal === id)
	// 	if(index >= 0){
	// 		this.sharedWithIds.splice(index, 1)
	// 	}
	// }

	isClientJournalInDraftForCoach(){
		return this.hasDisplayDestination(DisplayDestinationEnum.CLIENT_JOURNAL_FOR_COACH)
			&& !this.hasDisplayDestination(DisplayDestinationEnum.CLIENT_JOURNAL_FOR_CLIENT)
	}

	isFromClientJournalForCoach(){
		return this.hasDisplayDestination(DisplayDestinationEnum.CLIENT_JOURNAL_FOR_COACH)
	}

	isFromCoachJournal(){
		return this.hasDisplayDestination(DisplayDestinationEnum.COACH_JOURNAL)
	}

	enableCoachJournalDisplayDestination(enabled = true){
		this.enableDisplayDestination(enabled, DisplayDestinationEnum.COACH_JOURNAL)
	}

	enableClientJournalForCoachDisplayDestination(enabled = true){
		this.enableDisplayDestination(enabled, DisplayDestinationEnum.CLIENT_JOURNAL_FOR_COACH)
	}

	enableClientJournalForClientDisplayDestination(enabled = true){
		this.enableDisplayDestination(enabled, DisplayDestinationEnum.CLIENT_JOURNAL_FOR_CLIENT)
	}

	enableDisplayDestination(enabled, displayDestination){
		if(enabled){
			this.addDisplayDestination(displayDestination)
		} else {
			this.removeDisplayDestination(displayDestination)
		}
	}

	hasDisplayDestinationAny(){
		let keys = Object.keys(DisplayDestinationEnum)
		for(let key of keys){
			if(this.hasDisplayDestination(DisplayDestinationEnum[key])){
				return true
			}
		}
		return false
	}

	addDisplayDestination(displayDestination){
		this.displayDestination[displayDestination] = true
	}

	removeDisplayDestination(displayDestination){
		delete this.displayDestination[displayDestination]
	}

	hasDisplayDestination(displayDestination){
		return this.findDisplayDestinationIndex(displayDestination) >= 0
	}
	findDisplayDestinationIndex(displayDestination){
		return (this.displayDestinationArray || []).findIndex(id => id === displayDestination)
		// return this.sharedWithIds.findIndex(id => id === displayDestination)
		// return getDestinationArrayFromIndex(this.displayDestinationIndex).findIndex(item => item === displayDestination)
	}

	// beforeSaveToDb(options){
	// 	super.beforeSaveToDb(options)
	// 	if(!this.hasDisplayDestinationAny()){
	// 		throw Error("JournalEntry: displayDestination is not set - please make sure to add at least one value using enable****DisplayDestination() methods so it can be displayed in it's respective journal")
	// 	}
	// }

	summaryFormatted() {
		let summary = (this.summary || "").trim()
		let description = (this.description || "").trim()
		description = description.split("</p>")[0]

		summary = summary || stripHtmlTags(description)
		// summary = summary || getJournalEntryTypeLabel(this.journalEntry.type)
		// console.log("ellipsisString summaryFormatted = ", description)
		// console.log("ellipsisString: ", summary)
		summary = summary.split(/\n/)[0] //split by newline and take the first line only

		return ellipsisString(summary, 50, true)
	}

	static createFromFileType(fileType){

		switch (fileType){
			case QwoachFileType.LINK:
			case QwoachFileType.DRIVE_FILE:
				return new NoteJournalEntry()
			case QwoachFileType.LEGAL_DOC:
			case QwoachFileType.LEGAL_DOC_TEMPLATE:
				return new LegalDocJournalEntry()
			case QwoachFileType.FORM:
				return new FormJournalEntry()
		}

	}

	// migrateSchema(oldVersion, newVersion) {
	// 	// if(oldVersion < 2){
	// 	// 	versionIncrement++
	// 	// 	this.sharedWithIds.push(this.clientId)
	// 	// 	if(this.packageId){
	// 	// 		this.sharedWithIds.push(this.packageId)
	// 	// 	}
	// 	// 	console.log("JournalEntry migrateSchema: this.displayDestinationArray = ", this.displayDestinationArray, this)
	// 	// 	if(Array.isArray(this.displayDestinationArray)){
	// 	// 		this.sharedWithIds.push(...this.displayDestinationArray)
	// 	// 	} else {
	// 	// 		this.enableCoachJournalDisplayDestination(true)
	// 	// 	}
	// 	// }
	// 	if(this.schemaVersion === 2){
	// 		versionIncrement--
	// 		this.sharedWithIds = []
	// 		if(this.displayDestinationArray.length === 0){
	// 			this.enableCoachJournalDisplayDestination(true)
	// 		}
	// 		// this.sharedWithIds.push(this.clientId)
	// 		// if(this.packageId){
	// 		// 	this.sharedWithIds.push(this.packageId)
	// 		// }
	// 		// console.log("JournalEntry migrateSchema: this.displayDestinationArray = ", this.displayDestinationArray, this)
	// 		// if(Array.isArray(this.displayDestinationArray)){
	// 		// 	this.sharedWithIds.push(...this.displayDestinationArray)
	// 		// } else {
	// 		// 	this.enableCoachJournalDisplayDestination(true)
	// 		// }
	// 	}
	//
	// 	super.migrateSchema(oldVersion, newVersion)
	// }

}

export default JournalEntry
